import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import get from 'lodash/get';

import { useAccountGet } from '@sbv-clients/data-access-api';
import { useQueryParamStore } from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  useFiltersStore,
  useParamStore,
  useScheduleStatusInitStore,
} from '@sbv-clients/shared-state';
import { SelectOption, toReadableLabel } from '@sbv-clients/ui-shared';

import { useLoginToNewProfile } from '../../hooks/use-login-to-new-profile';

type ProfileOption = SelectOption<string>;

export const useProfileSwitch = () => {
  const paramStore = useParamStore();
  const scheduleStatusInitStore = useScheduleStatusInitStore();
  const queryClient = useQueryClient();

  const { data: accountData } = useAccountGet();
  const { mutateAsync: loginToNewProfile } = useLoginToNewProfile();
  const { onAuthSuccess } = useAuth();

  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const profileOptions = useMemo<ProfileOption[]>(() => {
    return (accountData?.profiles ?? []).map((profile) => ({
      id: profile.id as string,
      value: profile.id as string,
      label: toReadableLabel(
        profile?.databaseName || profile?.facilityName || ''
      ),
    }));
  }, [accountData?.profiles]);

  useEffect(() => {
    if (accountData?.tokenProfileId) {
      setSelectedProfileId(accountData.tokenProfileId);
    }
  }, [accountData?.tokenProfileId]);

  const handleSwitch = useCallback(
    async (nextProfileId: string) => {
      const previousProfileId = selectedProfileId;
      try {
        setSelectedProfileId(nextProfileId);
        setIsLoading(true);
        const { data } = await loginToNewProfile(nextProfileId);
        queryClient.clear();
        paramStore.clearAllPages();
        scheduleStatusInitStore.reset();
        const token = get(data, 'token', '') as string;
        const refreshToken = get(data, 'refreshToken', '') as string;
        onAuthSuccess(token, refreshToken);
        const { pathname } = window.location;
        window.location.replace(pathname.split('/').filter(Boolean)[0] || '/');

        useQueryParamStore.getState().resetAll();
        useFiltersStore.getState().resetAll();
      } catch {
        setSelectedProfileId(previousProfileId);
      } finally {
        setIsLoading(false);
      }
    },
    [
      loginToNewProfile,
      onAuthSuccess,
      paramStore,
      queryClient,
      scheduleStatusInitStore,
      selectedProfileId,
    ]
  );

  const switchProfile = useCallback(
    async (nextProfileId: string) => {
      if (!nextProfileId || nextProfileId === accountData?.tokenProfileId) {
        return;
      }
      await handleSwitch(nextProfileId);
    },
    [accountData?.tokenProfileId, handleSwitch]
  );

  const currentProfile =
    profileOptions.find((o) => o.value === selectedProfileId) ??
    (profileOptions.length === 1 ? profileOptions[0] : undefined);

  return {
    accountData,
    profileOptions,
    selectedProfileId,
    currentProfile,
    isLoading,
    switchProfile,
  };
};
